<template>
  
<FormKitLazyProvider>
<FormKit
    id="review-create"
    ref="reviewForm"
    v-model="review"
    name="review-create"
    class="review-create"
    type="form"
    :incomplete-message="$t('Sorry, not all fields are filled out correctly.')"
    :actions="false"
    @submit="submitReview"
  >
    <FormKit
      type="text"
      name="nickname"
      :label="$t('Name') + ' *'"
      validation="required"
      :validation-messages="{
        required: $t('This is a required field.'),
      }"
    />

    <FormKit
      type="text"
      name="summary"
      :label="$t('Title of your review') + ' *'"
      validation="required"
      :validation-messages="{
        required: $t('This is a required field.'),
      }"
    />

    <div class="review-create__heading heading heading--6">
      {{ $t('Tips for writing a review') }}
    </div>

    <ul>
      <li>
        {{ $t('Why do you like or not like the product?') }}
      </li>
      <li>
        {{ $t('The content of your review is about the product') }}
      </li>
    </ul>

    <FormKit
      type="textarea"
      name="description"
      :placeholder="$t('Indicate what you think of the product')"
      :label="$t('Your opinion in one sentence') + ' *'"
      rows="10"
      validation="required"
      :validation-messages="{
        required: $t('This is a required field.'),
      }"
    />

    <FormKit
      v-slot="{ items, node, value }"
      type="list"
      name="pros"
      dynamic
    >
      <FormKit
        v-for="(item, index) in items"
        :key="item"
        :index="index"
        :label="$t('Positive point') + ' *'"
        validation="required"
        :validation-label="$t('Positive point')"
        :validation-messages="{
          required: $t('This is a required field.'),
        }"
        suffix-icon="trash"
        :sections-schema="{
          suffixIcon: {
            // change wrapper to a button for accessibility
            $el: index === 0 ? null : 'button',
          },
        }"
        @suffix-icon-click="() => node.input(value.filter((_, i) => i !== index))"
      />
      <FormKit
        type="button"
        @click="() => node.input(value.concat(''))"
      >
        + {{ $t('Add another') }}
      </FormKit>
    </FormKit>

    <FormKit
      v-slot="{ items, node, value }"
      type="list"
      name="cons"
      dynamic
    >
      <FormKit
        v-for="(item, index) in items"
        :key="item"
        :index="index"
        :label="$t('Negative point')"
        suffix-icon="trash"
        :sections-schema="{
          suffixIcon: {
            // change wrapper to a button for accessibility
            $el: index === 0 ? null : 'button',
          },
        }"
        @suffix-icon-click="() => node.input(value.filter((_, i) => i !== index))"
      />
      <FormKit
        type="button"
        @click="() => node.input(value.concat(''))"
      >
        + {{ $t('Add another') }}
      </FormKit>
    </FormKit>

    <FormKit
      :label="$t('Average Rating') + ' *'"
      type="rating"
      name="score"
      validation="required|number|between:1,5"
      :validation-messages="{
        required: $t('This is a required field.'),
        between: $t('This is a required field.'),
      }"
    />

    <FormKitMessages hidden />

    <div class="formkit-actions">
      <button
        :disabled="!!loading"
        type="submit"
        class="button button--cta button--full"
        :class="{ 'button--loading': loading }"
      >
        <span>{{ $t('Submit review') }}</span>
        <IconLoader
          v-if="loading"
          class="icon icon--loader"
        />
        <IconArrow class="icon icon--right icon--arrow icon--arrow-right" />
      </button>
    </div>
  </FormKit>
</FormKitLazyProvider>

</template>

<script lang="ts" setup>
import { FormKitLazyProvider } from '@formkit/vue'
import type { ReviewData } from '~layers/app/types/review'
import createProductReview from '~/graphql/pdp/createProductReview.gql'

export type Props = {
  product: object
}

const props = defineProps<Props>()

const emit = defineEmits(['success'])

const review = reactive<ReviewData>({
  email: '',
  name: '',
  title: '',
  description: '',
  pros: [''],
  cons: [''],
  score: 0,
})

const addReviewInput = computed(() => {
  return {
    input: {
      nickname: review.nickname,
      rating: review.score,
      sku: props.product.sku,
      summary: review.summary,
      text: review.description,
      pros: review.pros,
      cons: review.cons,
    },
  }
})

const {
  mutate: addReviewMutation,
  loading,
  called: success,
} = useMutation(
  createProductReview,
  { clientId: 'customer' },
)

watch(success, (val) => {
  if (val) {
    emit('success', val)
  }
})

const submitReview = () => {
  addReviewMutation(addReviewInput.value)
}
</script>

<style>
@import url("~layers/app/components/Form/ReviewCreate.css");
</style>
